import { Link } from 'react-router';
import type { VoidFunctionComponent } from 'react';

import DocsLink from '../../generic/Link/DocsLink';
import Tabs from '../../generic/Tabs';

import DependencySelector from './DependencySelector';
import { TabIcons } from './ManagePackageLabelsContent';
import TabContent from './TabContent';
import { Tab } from './types';
import type { BulkPackageLabelState } from './packageLabelStateTypes';

import styles from './ModalManagePackageLabels.module.less';

type ManagePackageLabelsBulkContentProps = {
  // The state of the bulk package labels
  state: BulkPackageLabelState;

  // The locator of the user revision that imports the dependencies.
  parentRevisionLocator: string;

  // Enabled Tabs
  isGlobalEnabled?: boolean;
  isProjectEnabled?: boolean;
  isRevisionEnabled?: boolean;
};

/**
 * Content component for bulk package label assignment.
 * Displays dependency selector and tabs for assigning labels across scopes.
 */
export const ManagePackageLabelsBulkContent: VoidFunctionComponent<ManagePackageLabelsBulkContentProps> = ({
  parentRevisionLocator,
  state,
  isGlobalEnabled,
  isProjectEnabled,
  isRevisionEnabled,
}) => {
  return (
    <>
      <span className={styles.helpText}>
        You can create and manage labels in{' '}
        <Link className={styles.link} to="/account/settings/organization">
          Organization Settings
        </Link>
        . Learn more about using labels in our <DocsLink to="using-package-labels">docs</DocsLink>.
      </span>

      <DependencySelector
        selectedDependencies={state.selectedDependencies}
        onChange={state.setSelectedDependencies}
        parentRevisionLocator={parentRevisionLocator}
        isDisabled={state.hasNoPermission}
      />

      <Tabs>
        {state.tabs.tabs.map((tab) => {
          if (!isGlobalEnabled && tab.label === Tab.Global) {
            return null;
          }
          if (!isProjectEnabled && tab.label === Tab.Project) {
            return null;
          }
          if (!isRevisionEnabled && tab.label === Tab.Revision) {
            return null;
          }

          return (
            <Tabs.Tab
              key={tab.label}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...tab}
              icon={TabIcons[tab.label]}
            />
          );
        })}
      </Tabs>
      {isGlobalEnabled && (
        <TabContent
          mode="bulk"
          tab={Tab.Global}
          activeTab={state.tabs.activeTab}
          helpText="These labels will be applied globally to all selected dependencies, across all of your projects."
          parentRevisionLocator={parentRevisionLocator}
          state={state}
          labelsSelected={state.globalLabelsSelected}
          setLabelsSelected={state.setGlobalLabelsSelected}
          hasNoPermission={!state.hasGlobalPermission}
          useSpecificVersion={state.shouldUseGlobalSpecificVersion}
          setUseSpecificVersion={state.setShouldUseGlobalSpecificVersion}
        />
      )}
      {isProjectEnabled && (
        <TabContent
          mode="bulk"
          tab={Tab.Project}
          activeTab={state.tabs.activeTab}
          helpText="These labels will be applied to the selected dependencies in this project."
          parentRevisionLocator={parentRevisionLocator}
          state={state}
          labelsSelected={state.projectLabelsSelected}
          setLabelsSelected={state.setProjectLabelsSelected}
          hasNoPermission={!(state.hasLocalPermission || state.hasGlobalPermission)}
          useSpecificVersion={state.shouldUseProjectSpecificVersion}
          setUseSpecificVersion={state.setShouldUseProjectSpecificVersion}
        />
      )}
      {isRevisionEnabled && (
        <TabContent
          mode="bulk"
          tab={Tab.Revision}
          activeTab={state.tabs.activeTab}
          helpText="These labels are only visible in the current revision of this project. Note that revision scoped labels cannot be used for automatically ignoring issues with Issue Ignore Rules."
          parentRevisionLocator={parentRevisionLocator}
          state={state}
          labelsSelected={state.revisionLabelsSelected}
          setLabelsSelected={state.setRevisionLabelsSelected}
          hasNoPermission={!(state.hasLocalPermission || state.hasGlobalPermission)}
          useSpecificVersion={state.shouldUseRevisionSpecificVersion}
          setUseSpecificVersion={state.setShouldUseRevisionSpecificVersion}
        />
      )}
    </>
  );
};
